import AiAnalyticsApi from '../api/ai/AiAnalyticsApi';
import { INSTRUCTION_VISIBILITY, INSTRUCTION_WIRE_DEFAULTS } from '../constants/aiAnalytics';
import { onRequestError } from '../services/handlers';

import {
  ADD_INSTRUCTION,
  REMOVE_INSTRUCTION,
  SET_AI_ANALYTICS_ERROR,
  SET_AI_INSTRUCTIONS_LOADING,
  SET_INSTRUCTIONS,
  UPDATE_INSTRUCTION
} from './aiAnalyticsActionTypes';

export function normalizeInstruction(instruction) {
  return {
    ...instruction,
    id: instruction.id ?? instruction.instructionId,
    title: instruction.title ?? '',
    details: instruction.details ?? instruction.instructionDetails ?? instruction.instruction ?? '',
    visibility: instruction.visibility ?? INSTRUCTION_VISIBILITY.USER,
    createdAt: instruction.createdAt ?? instruction.created_at ?? instruction.createdTime ?? null,
    updatedAt: instruction.updatedAt ?? instruction.updated_at ?? null,
    createdBy: instruction.createdBy ?? instruction.created_by ?? null
  };
}

export function toInstructionWirePayload({ title, details, visibility }) {
  return { title, details, instructionDetails: details, visibility, ...INSTRUCTION_WIRE_DEFAULTS };
}

function isGlobalInstruction(visibility) {
  return visibility === INSTRUCTION_VISIBILITY.GLOBAL;
}

function setInstructionsLoading(dispatch, loading) {
  dispatch({ type: SET_AI_INSTRUCTIONS_LOADING, payload: loading });
}

function clearError(dispatch) {
  dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: null });
}

function reportInstructionsError(dispatch, error, message) {
  dispatch({ type: SET_AI_ANALYTICS_ERROR, payload: message });
  onRequestError(error, { customMessage: message });
}

export function fetchInstructions() {
  return async function(dispatch) {
    try {
      setInstructionsLoading(dispatch, true);
      const response = await AiAnalyticsApi.getInstructions();
      dispatch({ type: SET_INSTRUCTIONS, payload: (response.data || []).map(normalizeInstruction) });
    } catch (error) {
      reportInstructionsError(dispatch, error, 'Failed to fetch instructions');
    } finally {
      setInstructionsLoading(dispatch, false);
    }
  };
}

export function createInstruction(payload) {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      setInstructionsLoading(dispatch, true);
      const wirePayload = toInstructionWirePayload(payload);
      const response = isGlobalInstruction(payload.visibility)
        ? await AiAnalyticsApi.createGlobalInstruction(wirePayload)
        : await AiAnalyticsApi.createInstruction(wirePayload);
      const instruction = normalizeInstruction(response.data);
      dispatch({ type: ADD_INSTRUCTION, payload: instruction });
      return instruction;
    } catch (error) {
      reportInstructionsError(dispatch, error, 'Failed to create instruction');
      return null;
    } finally {
      setInstructionsLoading(dispatch, false);
    }
  };
}

export function updateInstruction(instructionId, payload, currentVisibility) {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      setInstructionsLoading(dispatch, true);
      const wirePayload = toInstructionWirePayload(payload);
      const involvesGlobal = isGlobalInstruction(payload.visibility) || isGlobalInstruction(currentVisibility);
      const response = involvesGlobal
        ? await AiAnalyticsApi.updateGlobalInstruction(instructionId, wirePayload)
        : await AiAnalyticsApi.updateInstruction(instructionId, wirePayload);
      const instruction = normalizeInstruction(response.data || { id: instructionId, ...payload });
      dispatch({ type: UPDATE_INSTRUCTION, payload: instruction });
      return instruction;
    } catch (error) {
      reportInstructionsError(dispatch, error, 'Failed to update instruction');
      return null;
    } finally {
      setInstructionsLoading(dispatch, false);
    }
  };
}

export function deleteInstruction(instructionId, visibility) {
  return async function(dispatch) {
    try {
      clearError(dispatch);
      setInstructionsLoading(dispatch, true);
      if (isGlobalInstruction(visibility)) {
        await AiAnalyticsApi.deleteGlobalInstruction(instructionId);
      } else {
        await AiAnalyticsApi.deleteInstruction(instructionId);
      }
      dispatch({ type: REMOVE_INSTRUCTION, payload: instructionId });
      return true;
    } catch (error) {
      reportInstructionsError(dispatch, error, 'Failed to delete instruction');
      return false;
    } finally {
      setInstructionsLoading(dispatch, false);
    }
  };
}
